import {useCallback, useEffect, useLayoutEffect, useState} from 'react';
import {useEvent} from './use-event';

type Options = {
  canToggle?: (currentState: boolean) => boolean;
  onToggle?: (currentState: boolean) => void;
  onTouched?: (currentState: boolean) => void;
  onSectionActiveChange?: (currentState: boolean) => void;
};

export function useSwitchSectionActive(
  preloadedSectionActive: boolean | undefined,
  {canToggle, onToggle, onSectionActiveChange, onTouched}: Options = {},
) {
  const [isSectionActive, setIsSectionActive] = useState(false);
  const [preloadedIsSendingEnabled, setPreloadedIsSendingEnabled] = useState<
    boolean | null
  >(null);
  const handleToggle = useEvent(onToggle);
  const handleOnTouched = useEvent(onTouched);
  const handleOnSectionActiveChange = useEvent(onSectionActiveChange);

  useLayoutEffect(
    function preloadIsSectionActive() {
      if (preloadedSectionActive !== undefined) {
        setPreloadedIsSendingEnabled(preloadedSectionActive);
        setIsSectionActive(preloadedSectionActive);
      } else {
        setPreloadedIsSendingEnabled(false);
      }
    },
    [preloadedSectionActive],
  );

  const toggleIsSectionActive = useCallback(() => {
    const isTogglingDisabled = canToggle && !canToggle(isSectionActive);

    if (isTogglingDisabled) {
      return;
    }

    setIsSectionActive(prevState => !prevState);
    handleToggle(!isSectionActive);
  }, [canToggle, isSectionActive, handleToggle]);

  const isSectionActiveTouched = isSectionActive !== !!preloadedIsSendingEnabled;

  useEffect(() => {
    handleOnTouched(isSectionActiveTouched);
  }, [handleOnTouched, isSectionActiveTouched]);

  useEffect(() => {
    handleOnSectionActiveChange(isSectionActive);
  }, [handleOnSectionActiveChange, isSectionActive]);

  return {
    isSectionActive:
      preloadedIsSendingEnabled !== null ? isSectionActive : !!preloadedSectionActive,
    toggleIsSectionActive,
    setIsSectionActive,
    isSectionActiveTouched,
  };
}
